function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from "react";
import { ScrollContainer } from "sprout-ui-dom/dist/ScrollContainer";
import Downshift from "downshift";
import { useMove_CreateTopicMutation } from "sprout-graphql";
import { Flex } from "sprout-ui-primitives";
import Fuse from "fuse.js";
import useProcessTopicsToGroups, { GroupType } from "sprout-hooks/dist/useProcessTopicsToGroups";
import useSpecialTopic from "@sprout/hooks/dist/useSpecialTopic";
import MoveItemsDialogGroups from "./MoveItemsDialog_Groups";
import MoveItemsDialogInput from "./MoveItemsDialog_Input";
import { MoveItemsDialogView, useMoveItemsDialog } from "./MoveItemsDialogContext";
import MoveItemsDialogReset from "./MoveItemsDialog_Reset";
import MoveItemsDialogAdd from "./MoveItemsDialog_Add";
import useTrackMoveHistory from "./useTrackMoveHistory";
import useMoveItems from "./graphql/mutations/useMoveItems";
/**
 * A function to filter each groups based on the result provided by `Fuse.js`
 */ const processFuseResult = (results)=>{
    return results.map((result)=>{
        const matches = (result.matches || []).map(({ refIndex  })=>{
            return refIndex;
        });
        const { item  } = result;
        const { items  } = item;
        return _objectSpread({}, item, {
            // https://github.com/microsoft/TypeScript/issues/36390
            // @ts-ignore
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            items: items.filter((_, index)=>matches.includes(index))
        });
    });
};
const stateReducer = (state, changes)=>{
    switch(changes.type){
        case Downshift.stateChangeTypes.blurInput:
        case Downshift.stateChangeTypes.mouseUp:
        case Downshift.stateChangeTypes.touchEnd:
            /**
       * `touchEnd` is needed mobile to preserve the input text on blur.
       */ return _objectSpread({}, changes, {
                inputValue: state.inputValue
            });
        case Downshift.stateChangeTypes.changeInput:
            if (changes.inputValue) /**
         * Aggressively select the first item when the input changes
         * so the user can immediately save to it.
         */ return _objectSpread({}, changes, {
                highlightedIndex: 0
            });
            return changes;
        default:
            return changes;
    }
};
const MoveItemsDialogTopics = (props)=>{
    const { topicTree , topicsQueryData , onRequestClose , onSuccess  } = props;
    const { view , setView , currentTopicId , setCurrentTopicId , initialCurrentTopicId , itemIds , itemNodeIds ,  } = useMoveItemsDialog();
    const [error, setError] = useState(false);
    const [dirty, setDirty] = useState(false);
    const inputValueRef = useRef("");
    const handleInputValueChange = useCallback((inputValue)=>{
        // reset error state
        if (!inputValue && inputValueRef.current) setError(false);
        inputValueRef.current = inputValue;
        if (inputValue.length > 0) setDirty(true);
        else setDirty(false);
    }, []);
    const [currentUserRecentTopics, currentUserTeams, currentUserTopics] = useMemo(()=>{
        var ref, ref1, ref2;
        return [
            (topicsQueryData === null || topicsQueryData === void 0 ? void 0 : (ref = topicsQueryData.moveHistories) === null || ref === void 0 ? void 0 : ref.nodes) || [],
            (topicsQueryData === null || topicsQueryData === void 0 ? void 0 : (ref1 = topicsQueryData.currentUser) === null || ref1 === void 0 ? void 0 : ref1.teams.nodes) || [],
            (topicsQueryData === null || topicsQueryData === void 0 ? void 0 : (ref2 = topicsQueryData.currentUser) === null || ref2 === void 0 ? void 0 : ref2.topics.nodes) || [], 
        ];
    }, [
        topicsQueryData
    ]);
    const { readLaterTopic , myTasksTopic , profileTopic  } = useSpecialTopic();
    const readLaterTopicId = readLaterTopic === null || readLaterTopic === void 0 ? void 0 : readLaterTopic.id;
    const myTasksTopicId = myTasksTopic === null || myTasksTopic === void 0 ? void 0 : myTasksTopic.id;
    const profileTopicId = profileTopic === null || profileTopic === void 0 ? void 0 : profileTopic.id;
    const defaultGroups = useProcessTopicsToGroups({
        moveHistories: currentUserRecentTopics,
        personalTopics: currentUserTopics,
        teams: currentUserTeams,
        topicTree,
        topicsToFilter: currentTopicId ? [
            currentTopicId
        ] : []
    });
    const recentGroup = useMemo(()=>defaultGroups.find((group)=>group.type === GroupType.RECENT), [
        defaultGroups
    ]);
    const [lastRecentTopic] = recentGroup ? recentGroup.items : [];
    const lastRecentTopicId = lastRecentTopic === null || lastRecentTopic === void 0 ? void 0 : lastRecentTopic.id;
    const filteredGroups = useMemo(()=>{
        if (view === MoveItemsDialogView.CLEAN) return defaultGroups;
        else return defaultGroups.filter((group)=>{
            return group.type === GroupType.CURRENT;
        });
    }, [
        defaultGroups,
        view
    ]);
    const searcher = useMemo(()=>{
        const options = {
            includeMatches: true,
            keys: [
                "items.title",
                "items.topic.title"
            ],
            threshold: 0.1,
            findAllMatches: true
        };
        return new Fuse(filteredGroups, options);
    }, [
        filteredGroups
    ]);
    const itemToString = useCallback((item)=>{
        if (typeof item === "string") return item;
        return "";
    }, []);
    const moveItemsMutation = useMoveItems();
    const trackMoveHistory = useTrackMoveHistory();
    const moveItems = useCallback(function() {
        var _ref = _asyncToGenerator(function*(topicId) {
            onRequestClose();
            try {
                yield moveItemsMutation({
                    itemNodeIds,
                    itemIds,
                    topicId
                });
                if (onSuccess) onSuccess(itemIds);
                trackMoveHistory({
                    currentItemId: topicId,
                    lastItemId: lastRecentTopicId
                });
            } catch (err) {
                console.error(err);
            }
        });
        return function(topicId) {
            return _ref.apply(this, arguments);
        };
    }(), [
        itemIds,
        itemNodeIds,
        lastRecentTopicId,
        moveItemsMutation,
        onRequestClose,
        onSuccess,
        trackMoveHistory, 
    ]);
    const [createTopicMutation] = useMove_CreateTopicMutation();
    const handleChange = useCallback((selectedItem, stateAndHelpers)=>{
        if (selectedItem === null) return;
        if (typeof selectedItem === "string") {
            setView(MoveItemsDialogView.ADD);
            /**
         * Select the first item if there's already an input value
         * so the user can immediately save to it.
         */ if (stateAndHelpers.inputValue) stateAndHelpers.setHighlightedIndex(0);
            return;
        }
        const { type  } = selectedItem;
        if (type === GroupType.SPECIAL_TREE) {
            const { special  } = selectedItem;
            let topicId;
            if (special === "MY_TASKS") topicId = myTasksTopicId;
            else if (special === "INBOX") topicId = readLaterTopicId;
            else if (special === "PROFILE") topicId = profileTopicId;
            if (topicId) moveItems(topicId);
            return;
        }
        const topicId = "topicId" in selectedItem ? selectedItem.topicId : undefined;
        if (typeof topicId !== "number") return;
        if (type === GroupType.CURRENT) {
            if (view === MoveItemsDialogView.ADD) {
                const { inputValue  } = selectedItem;
                if (inputValue) createTopicMutation({
                    variables: {
                        title: inputValue,
                        parentTopicId: topicId
                    }
                }).then(({ data  })=>{
                    var ref, ref3;
                    const createdTopicId = data === null || data === void 0 ? void 0 : (ref = data.createItem) === null || ref === void 0 ? void 0 : (ref3 = ref.item) === null || ref3 === void 0 ? void 0 : ref3.id;
                    if (createdTopicId) moveItems(createdTopicId);
                }).catch((err)=>{
                    console.error(err);
                });
                else // no branch name, show error
                setError(true);
                return;
            }
        }
        if ([
            GroupType.PERSONAL,
            GroupType.TEAM
        ].includes(type)) {
            const { hasChildren  } = selectedItem;
            if (hasChildren) {
                setCurrentTopicId(topicId);
                setView(MoveItemsDialogView.SCOPED);
                return;
            }
        }
        moveItems(topicId);
    }, [
        createTopicMutation,
        moveItems,
        myTasksTopicId,
        profileTopicId,
        readLaterTopicId,
        setCurrentTopicId,
        setView,
        view, 
    ]);
    return /*#__PURE__*/ _jsx(Downshift, {
        defaultIsOpen: true,
        onInputValueChange: handleInputValueChange,
        itemToString: itemToString,
        onChange: handleChange,
        stateReducer: stateReducer,
        initialHighlightedIndex: (topicTree === null || topicTree === void 0 ? void 0 : topicTree.id) === initialCurrentTopicId ? undefined : 0,
        children: ({ getRootProps , getInputProps , getMenuProps , getItemProps , inputValue , clearSelection ,  })=>{
            const groupsToRender = view === MoveItemsDialogView.ADD ? filteredGroups : inputValue ? processFuseResult(searcher.search(inputValue.trim())) : filteredGroups;
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(Flex, _objectSpread({
                        flexDirection: "column",
                        minHeight: "0"
                    }, getRootProps(undefined, {
                        suppressRefError: true
                    }), {
                        children: [
                            /*#__PURE__*/ _jsx(MoveItemsDialogInput, {
                                view: view,
                                error: error,
                                getInputProps: getInputProps,
                                onRequestClose: onRequestClose
                            }),
                            /*#__PURE__*/ _jsx(ScrollContainer, _objectSpread({}, getMenuProps(), {
                                children: /*#__PURE__*/ _jsx(MoveItemsDialogGroups, {
                                    view: view,
                                    hideDepth: dirty,
                                    initialCurrentTopicId: initialCurrentTopicId,
                                    inputValueRef: inputValueRef,
                                    getItemProps: getItemProps,
                                    groups: groupsToRender
                                })
                            })),
                            view !== MoveItemsDialogView.ADD && /*#__PURE__*/ _jsx(MoveItemsDialogAdd, {
                                inputValue: inputValue || undefined,
                                getItemProps: getItemProps
                            })
                        ]
                    })),
                    /*#__PURE__*/ _jsx(MoveItemsDialogReset, {
                        view: view,
                        clearSelection: clearSelection
                    })
                ]
            });
        }
    });
};
export default MoveItemsDialogTopics;
